// A store, and deliberately not a state library.
//
// Callers subscribe and read; nobody outside this package pokes the
// engine. That is the whole reason it exists: a component that can call
// `subscriber.latest()` is a component that can render half an update, and
// the honesty model is only worth anything if every screen reads the same
// derived snapshot.

export type Unsubscribe = () => void;

export interface ReadableStore<T> {
  get(): T;
  subscribe(listener: (value: T) => void): Unsubscribe;
}

export interface WritableStore<T> extends ReadableStore<T> {
  set(value: T): void;
  update(mutate: (value: T) => T): void;
}

export function createStore<T>(initial: T): WritableStore<T> {
  let value = initial;
  const listeners = new Set<(next: T) => void>();
  return {
    get: () => value,
    set(next: T) {
      if (Object.is(next, value)) return;
      value = next;
      for (const listener of [...listeners]) listener(value);
    },
    update(mutate) {
      this.set(mutate(value));
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    }
  };
}

/** A derived read-only view. Recomputed on every push, memoised on identity. */
export function derived<T, U>(source: ReadableStore<T>, project: (value: T) => U): ReadableStore<U> {
  return {
    get: () => project(source.get()),
    subscribe(listener) {
      let last = project(source.get());
      return source.subscribe(value => {
        const next = project(value);
        if (Object.is(next, last)) return;
        last = next;
        listener(next);
      });
    }
  };
}
